
import Component from 'vue-class-component';
import Vue from 'vue';
import { namespace } from 'vuex-class';
import { LoanTransactionWithDetails } from '@/loan/types/loan-transaction-with-details';
import { LoanTransactionStatusId } from '@/loan/enums/loan-transaction-status-id';
import moment from 'moment';
import { FORM_DEFAULT_SELECT_OPTION } from '@/core/constants/form-default-select-option';
import { CoreHelper } from '@/core/helper';
import { LoanTransactionsFilterBy } from '@/loan/enums/loan-transactions-filter-by';
import { LoanTransactionDetailId } from '@/loan/enums/loan-transaction-detail-id';
import { LOAN_TRANSACTION_HEADERS } from '@/loan/constants/loan-transaction-headers';
import { LoanHelper } from '@/loan/helper';
import { UtilHelper } from '@/util/helper';

const loanNamespace = namespace('loan');

const pendingHeaders = [
	{
		text: 'Date',
		align: 'start',
		value: 'effectiveDate',
	},
	{
		text: 'Description',
		sortable: false,
		value: 'description',
	},
	{
		text: 'Amount',
		sortable: false,
		value: 'amount',
	},
];

const filterByOptions = [
	FORM_DEFAULT_SELECT_OPTION,
	...CoreHelper.createFormSelectOptionsFromPrimitives([
		'Year',
		'Status',
		'Type',
	]),
];

const optionOptions = [FORM_DEFAULT_SELECT_OPTION];
const TEST_ID = 'Kloans-Loan-LoanTransactions';

@Component
export default class LoanTransactions extends Vue {
	public TEST_ID = TEST_ID;

	public filterByModel = FORM_DEFAULT_SELECT_OPTION.value;

	public filterByOptions = filterByOptions;

	public formatAmount = LoanHelper.getTransactionAmountText;

	public formatDate = (date: string) => moment(date).format('MM/DD/YYYY');

	public formatDetail = LoanHelper.resolveTransactionDetailId;

	public formatDollar = UtilHelper.formatDollar;

	public formatStatus = LoanHelper.resolveTransactionStatusId;

	public formatType = LoanHelper.resolveTransactionTypeId;

	public optionModel = FORM_DEFAULT_SELECT_OPTION.value;

	public optionOptions = optionOptions;

	public pendingHeaders = pendingHeaders;

	@loanNamespace.Getter('pendingTransactions')
	public pendingTransactions!: LoanTransactionWithDetails[];

	public postedHeaders = LOAN_TRANSACTION_HEADERS;

	public nonPendingTransactions: LoanTransactionWithDetails[] = [];

	@loanNamespace.State('transactions')
	private transactions!: LoanTransactionWithDetails[];

	get isClearFiltersButtonShown(): boolean {
		const isFilterByDefault = this.filterByModel === FORM_DEFAULT_SELECT_OPTION.value;
		return !isFilterByDefault;
	}

	mounted(): void {
		this.nonPendingTransactions = this.getNonPendingTransactions();
	}

	updated(): void {
		this.filterTransactionTableDropdowns();
	}

	public onClearFilters(): void {
		this.filterByModel = FORM_DEFAULT_SELECT_OPTION.value;
		this.optionModel = FORM_DEFAULT_SELECT_OPTION.value;
		this.optionOptions = [FORM_DEFAULT_SELECT_OPTION];
		this.nonPendingTransactions = this.getNonPendingTransactions();
	}

	public onFilterByChange(value: string): void {
		this.nonPendingTransactions = this.getNonPendingTransactions();

		if (value === FORM_DEFAULT_SELECT_OPTION.value) {
			this.optionModel = FORM_DEFAULT_SELECT_OPTION.value;
			this.optionOptions = [FORM_DEFAULT_SELECT_OPTION];
			return;
		}

		if (value === LoanTransactionsFilterBy.YEAR) {
			this.optionOptions = LoanHelper.getTransactionsYearSelectOptions(
				this.nonPendingTransactions,
				FORM_DEFAULT_SELECT_OPTION,
			);
			this.optionModel = FORM_DEFAULT_SELECT_OPTION.value;
			return;
		}

		if (value === LoanTransactionsFilterBy.STATUS) {
			this.optionOptions = LoanHelper.getTransactionsStatusSelectOptions(
				this.nonPendingTransactions,
				FORM_DEFAULT_SELECT_OPTION,
			);
			this.optionModel = FORM_DEFAULT_SELECT_OPTION.value;
			return;
		}

		if (value === LoanTransactionsFilterBy.TYPE) {
			this.optionOptions = LoanHelper.getTransactionsTypeSelectOptions(
				this.nonPendingTransactions,
				FORM_DEFAULT_SELECT_OPTION,
			);
			this.optionModel = FORM_DEFAULT_SELECT_OPTION.value;
		}
	}

	public onOptionChange(value: string): void {
		this.nonPendingTransactions = this.getNonPendingTransactions();

		if (value === FORM_DEFAULT_SELECT_OPTION.value) {
			return;
		}

		if (this.filterByModel === LoanTransactionsFilterBy.YEAR) {
			this.nonPendingTransactions = this.nonPendingTransactions
				.filter((transaction) => LoanHelper.getTransactionYear(transaction) === value);
			return;
		}

		if (this.filterByModel === LoanTransactionsFilterBy.STATUS) {
			this.nonPendingTransactions = this.nonPendingTransactions
				.filter((transaction) => transaction.status === Number(value));
			return;
		}

		if (this.filterByModel === LoanTransactionsFilterBy.TYPE) {
			this.nonPendingTransactions = this.nonPendingTransactions
				.filter((transaction) => transaction.transactionType === Number(value));
		}
	}

	public onPaginate(): void {
		// filterTransactionTableDropdowns will not be able to interact with the correct DOM Elements
		// after an update without being inside of nextTick.
		this.$nextTick(() => {
			this.filterTransactionTableDropdowns();
		});
	}

	/**
	 * Show/Hide the dropdown icons on the Vuetify data-table based on if a
	 * transaction has loanTransactionDetails.
	 * This is a Vuetify data-table limitation workaround
	 */
	private filterTransactionTableDropdowns(): void {
		const transactionsTable: HTMLElement = (this.$refs.transactionsTable as any).$el;

		if (!transactionsTable) {
			return;
		}

		try {
			const transactionIdentifierElements: HTMLElement[] = Array.from(transactionsTable.querySelectorAll('.transaction-identifier'));

			transactionIdentifierElements.forEach((element) => {
				const { id } = element.dataset;
				const transaction = this.transactions.find((t) => t.id === id);
				const parentTd = element.parentElement;
				const parentTr = parentTd?.parentElement;
				const dropdownTd = parentTr?.querySelector('td');
				const button = dropdownTd?.querySelector('button.fa-caret-down');
				(button as HTMLElement).setAttribute('data-testid', `${id}-button-toggle`);

				const hasDetails = transaction && transaction.loanTransactionDetails.length > 0;

				if (button && !hasDetails) {
					(button as HTMLElement).style.display = 'none';
				}
			});
		} catch (error) {
			console.error(error);
		}
	}

	private getNonPendingTransactions(): LoanTransactionWithDetails[] {
		const transactions = this.transactions.filter((t) => t.status !== LoanTransactionStatusId.PENDING);

		transactions.forEach((t) => {
			// put all fees into a sum
			let totalFees = 0;
			t.loanTransactionDetails.forEach((f) => {
				if (f.transactionDetailTypeId === LoanTransactionDetailId.PAYMENT_FEES) {
					totalFees += Number(f.amount);
				}
			});

			t.loanTransactionDetails = t.loanTransactionDetails
				.filter((d) => {
					const isUnpaidInterest = d.transactionDetailTypeId === LoanTransactionDetailId.UNPAID_INTEREST;
					const isNonLoanItem = d.transactionDetailTypeId === LoanTransactionDetailId.NON_LOAN_ITEMS;
					const isPaymentFee = d.transactionDetailTypeId === LoanTransactionDetailId.PAYMENT_FEES;

					return !isUnpaidInterest && !isNonLoanItem && !isPaymentFee;
				});

			if (totalFees > 0) 			{
				t.loanTransactionDetails.push({
					amount: totalFees.toString(),
					transactionDetailTypeId: LoanTransactionDetailId.PAYMENT_FEES,
					id: '',
					loanId: '',
					assetId: '',
					loanTransactionId: '',
				});
			}
		});

		return transactions;
	}
}

